import { Popover } from '@headlessui/react';
import ToastStack from 'components/desktop/shared/toast-stack';
import {
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconContactChat,
  IconHeart,
  IconHeartOutline,
  IconLoading,
  IconPanHorizontal,
  IconPanVertical,
  IconPaperPlane,
  IconShareVariantOutline,
  IconSmile,
  IconStairs,
  IconVectorSquareClose,
} from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { CommentForm, commentFormDefaultValues } from 'forms/temp/comment';
import { SITE_MAP } from 'globalConstants';
import { Post } from 'graphql/main/mutations/temp/create-post';
import {
  CREATE_COMMENT,
  CreateCommentData,
  CreateCommentVariables,
} from 'graphql/main/mutations/temp/createComment';
import { FileWithFullUrls } from 'graphql/main/queries';
import {
  COMMENTS,
  Comment,
  CommentsData,
  CommentsVariables,
} from 'graphql/main/queries/temp/comments';
import { useFlexMutation, useFlexQuery, useToast } from 'hooks';
import { gt, isEmpty, isEqual, isString, lt, map, size, toString, trim } from 'lodash';
import moment from 'moment-timezone';
import Link from 'next/link';
import { Fragment, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FreeMode, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import { timeSince } from 'utils';
import CommentTree from '../../../../../shared/temp/comment-tree';

const DEFAULT_LIMIT_COMMENT = 10;

interface Props {
  post?: Post;
  onLike?: (event: MouseEvent<HTMLButtonElement>) => Promise<void>;
  onCompleteCommenting?: () => void;
  onClose?: () => void;
}

const CommentModal = ({ post, onLike, onCompleteCommenting, onClose }: Props) => {
  const [privateComments, setPrivateComments] = useState<Comment[]>([]);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [bigControlledSwiper, setBigControlledSwiper] = useState<SwiperType>();
  const [smallControlledSwiper, setSmallControlledSwiper] = useState<SwiperType>();
  const { id, images, commentCount } = post ?? {};
  const { toasts, addToast, removeToast } = useToast();
  const [showingEmoji, setShowingEmoji] = useState<boolean>(false);
  const { currentUser } = useAuthenticationContext();
  const [page, setPage] = useState(1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);
  const { control, getValues, setValue } = useForm<CommentForm>({
    defaultValues: commentFormDefaultValues,
  });
  const bodyWatch = useWatch({
    control,
    name: 'body',
  });
  const { data: commentsData, loading: loadingComments } = useFlexQuery<
    CommentsData,
    CommentsVariables
  >('main', COMMENTS, {
    variables: {
      postId: isString(id) ? id : '',
      page,
      limit: DEFAULT_LIMIT_COMMENT,
    },
    skip: !isString(id),
  });
  const comments = commentsData?.comments?.data || [];
  const [createComment] = useFlexMutation<CreateCommentData, CreateCommentVariables>(
    'main',
    CREATE_COMMENT,
  );

  const changeBigSlide = (swiper: SwiperType) => {
    smallControlledSwiper?.slideTo?.(swiper.realIndex, 0);
    setActiveImageIndex(swiper.realIndex);
  };
  const selectImage = (index: number) => {
    bigControlledSwiper?.slideTo?.(index + 1, 0);
    setActiveImageIndex(index);
  };
  const chooseEmoji = (_: EmojiClickData, event: any) => {
    setValue('body', getValues('body') + event.emoji);
    inputRef.current?.focus();
  };
  const keyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (isEqual(event.key, 'Enter')) {
      const { body } = getValues();
      if (id && body && !isEmpty(trim(body))) {
        const { data: createCommentData, errors: createCommentErrors } = await createComment({
          variables: {
            body: trim(body),
            postId: id,
          },
        });
        if (createCommentErrors) {
          addToast('error', createCommentErrors?.[0]?.message);
        } else {
          if (createCommentData?.createComment?.data) {
            setPrivateComments([createCommentData.createComment.data, ...privateComments]);
          }
          divRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          await onCompleteCommenting?.();
        }
      }
      setValue('body', '');
    }
  };

  useEffect(() => {
    setPrivateComments([...privateComments, ...comments]);
  }, [comments]);

  return (
    <Fragment>
      <div className='relative fixed top-0 left-0 flex h-screen w-screen bg-paper'>
        <div className='absolute top-0 right-0 w-[414px] px-[12px] py-[16px] flex items-center justify-between'>
          <div className='flex items-center space-x-[10px] overflow-hidden'>
            <Link
              href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.SOCIAL_PROFILE}/${post?.user?.id}`}
            >
              <div className='h-[36px] w-[36px] cursor-pointer overflow-hidden rounded-full'>
                <img
                  src={post?.user?.avatar?.originalUrl ?? '/images/default-avatar.svg'}
                  alt={post?.user?.fullName}
                  loading='lazy'
                  className='h-full w-full object-cover'
                />
              </div>
            </Link>
            <div className='flex flex-col justify-between h-[36px]'>
              <Link
                href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.SOCIAL_PROFILE}/${post?.user?.id}`}
                className='hover:underline'
              >
                <span className='font-[600] break-words line-clamp-1'>{post?.user?.fullName}</span>
              </Link>
              <span className='text-[12px] leading-[15px] text-text2'>
                {timeSince(moment(post?.createdAt).toDate())}
              </span>
            </div>
          </div>
          <button type='button' className='mt-[-5px] mr-[-6px] self-start' onClick={onClose}>
            <IconClose className='min-h-[24px] min-w-[24px] text-text2' />
          </button>
        </div>
        <div
          className='relative flex w-full flex-col justify-between bg-cover bg-center bg-no-repeat'
          style={{
            backgroundImage: `url("${
              images ? images[activeImageIndex].originalUrl : '/images/error-image.svg'
            }")`,
          }}
        >
          <div className='absolute left-0 top-0 w-full h-full bg-[#000000]/[.3] backdrop-blur-[50px]' />
          <div className='relative flex h-full w-full items-center justify-center'>
            {gt(size(images), 1) && (
              <button
                id='image-viewer-swiper-back'
                type='button'
                className='absolute left-[8px] top-1/2 z-[2] flex h-[32px] w-[32px] -translate-y-1/2 items-center justify-center rounded-[6px] bg-[#000000]/[.2] p-[8px] opacity-90 hover:opacity-100 screen-640:left-[16px]'
              >
                <IconChevronLeft className='min-h-[24px] min-w-[24px] text-[#ffffff]' />
              </button>
            )}
            <div className='flex h-full w-full items-center justify-center'>
              <Swiper
                threshold={10}
                draggable
                loop
                slidesPerView={1}
                initialSlide={activeImageIndex}
                navigation={{
                  prevEl: '#image-viewer-swiper-back',
                  nextEl: '#image-viewer-swiper-forward',
                }}
                modules={[Navigation]}
                className='w-[calc((100vw-414px)*0.8)]'
                onSwiper={(swiper: SwiperType) => setBigControlledSwiper(swiper)}
                onSlideChange={changeBigSlide}
              >
                {map(
                  isEmpty(images)
                    ? [{ fileId: '', originalUrl: '/images/error-image.svg' }]
                    : images,
                  (image, imageIndex) => (
                    <SwiperSlide key={`${image.fileId}-${imageIndex}`}>
                      <div className='flex h-[calc(100vh-140px)] items-center justify-center'>
                        <img
                          src={image.originalUrl ?? '/images/error-image.svg'}
                          alt='current-gallery'
                          loading='lazy'
                          className='object-cover'
                        />
                      </div>
                    </SwiperSlide>
                  ),
                )}
              </Swiper>
            </div>
            {gt(size(images), 1) && (
              <button
                id='image-viewer-swiper-forward'
                type='button'
                className='absolute right-[8px] top-1/2 z-[2] flex h-[32px] w-[32px] -translate-y-1/2 items-center justify-center rounded-[6px] bg-[#000000]/[.2] p-[8px] opacity-90 hover:opacity-100 screen-640:right-[16px]'
              >
                <IconChevronRight className='min-h-[24px] min-w-[24px] text-[#ffffff]' />
              </button>
            )}
          </div>
          <div className='min-h-[80px] p-[8px]'>
            <Swiper
              threshold={10}
              draggable
              freeMode
              slidesPerView='auto'
              initialSlide={activeImageIndex}
              spaceBetween={8}
              modules={[FreeMode]}
              onSwiper={(swiper: SwiperType) => setSmallControlledSwiper(swiper)}
            >
              {map(images, (image, imageIndex) => (
                <SwiperSlide
                  key={`${image.fileId}-${imageIndex}`}
                  className='w-[auto_!important]'
                  onClick={() => selectImage(imageIndex)}
                >
                  <div
                    className={`h-[64px] w-[112px] cursor-pointer overflow-hidden rounded-[6px] hover:opacity-100 ${
                      isEqual(activeImageIndex, imageIndex)
                        ? 'border-2 border-stroke opacity-100'
                        : 'opacity-50'
                    }`}
                  >
                    <img
                      src={image.originalUrl ?? '/images/error-image.svg'}
                      alt={`gallery-${imageIndex}`}
                      loading='lazy'
                      className='h-full w-full object-cover'
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
        <div className='pt-[68px] pb-[72px]'>
          <div
            ref={divRef}
            className='flex h-full w-[414px] min-w-[414px] flex-col space-y-[16px] px-[12px] pb-[16px] overflow-y-auto'
          >
            <span className='text-[16px] min-h-[20px] font-[600] capitalize leading-[20px] line-clamp-2 break-words'>
              {post?.title}
            </span>
            <div className='flex items-center space-x-[12px] w-full min-h-[30px]'>
              <div className='flex justify-center space-x-[8px] whitespace-nowrap w-[calc((100%-75px)/4)]'>
                <IconPanHorizontal className='h-[20px] min-w-[20px] w-[20px] text-primary' />
                <span className='truncate'>
                  {post?.width?.from ? `${post.width.from.toLocaleString()}m` : '-'}
                </span>
              </div>
              <div className='w-[1px] h-full bg-stroke' />
              <div className='flex justify-center space-x-[8px] whitespace-nowrap w-[calc((100%-75px)/4)]'>
                <IconPanVertical className='h-[20px] min-w-[20px] w-[20px] text-primary' />
                <span className='truncate'>
                  {post?.length?.from ? `${post.length.from.toLocaleString()}m` : '-'}
                </span>
              </div>
              <div className='w-[1px] h-full bg-stroke' />
              <div className='flex justify-center space-x-[8px] whitespace-nowrap w-[calc((100%-75px)/4)]'>
                <IconVectorSquareClose className='h-[20px] min-w-[20px] w-[20px] text-primary' />
                <span className='truncate'>
                  {post?.area?.from ? `${post.area.from.toLocaleString()} m\u00B2` : '-'}
                </span>
              </div>
              <div className='w-[1px] h-full bg-stroke' />
              <div className='flex justify-center space-x-[8px] whitespace-nowrap w-[calc((100%-75px)/4)]'>
                <IconStairs className='h-[20px] min-w-[20px] w-[20px] text-primary' />
                <span className='truncate'>
                  {post?.floorCount?.from ? `${post.floorCount.from.toLocaleString()} tầng` : '-'}
                </span>
              </div>
            </div>
            <div className='flex flex-col'>
              <div
                className='w-full truncate whitespace-pre-wrap leading-[20px] line-clamp-[6]'
                dangerouslySetInnerHTML={{ __html: toString(post?.body) }}
              />
              <Link
                href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.REAL_ESTATE_POST.INDEX}/${post?.id}`}
              >
                <span className='text-[12px] leading-[20px] text-primary font-[600] underline'>
                  Xem chi tiết
                </span>
              </Link>
            </div>
            <div className='flex justify-end space-x-[12px] text-text2 text-[12px] leading-[15px]'>
              <span>{post?.likeCount} Thích</span>
              <span>{commentCount} Bình luận</span>
            </div>
            <div className='flex justify-between items-center p-[12px] border-y border-stroke'>
              <button
                type='button'
                className={`flex items-center space-x-[8px] ${
                  post?.isLiked ? 'text-[#ff4f66]' : 'text-text2 hover:text-primary'
                }`}
                onClick={onLike}
              >
                {post?.isLiked ? (
                  <IconHeart className='h-[24px] w-[24px]' />
                ) : (
                  <IconHeartOutline className='h-[24px] w-[24px]' />
                )}
                <span className='min-w-[60px]'>{post?.isLiked ? 'Bỏ thích' : 'Yêu thích'}</span>
              </button>
              <button
                type='button'
                className='flex items-center space-x-[8px] text-text2 hover:text-primary'
                onClick={() => inputRef.current?.focus()}
              >
                <IconContactChat className='h-[24px] w-[24px] hover:text-primary' />
                <span className='min-w-[60px]'>Bình luận</span>
              </button>
              <button
                type='button'
                className='flex items-center space-x-[8px] text-text2 hover:text-primary'
              >
                <IconShareVariantOutline className='h-[20px] w-[20px]' />
                <span className='min-w-[60px]'>Chia sẻ</span>
              </button>
            </div>
            <div className='flex flex-col space-y-[12px]'>
              {!loadingComments && isEmpty(privateComments) ? (
                <div className='flex w-full flex-col items-center justify-center'>
                  <div className='h-[240px] w-[300px] bg-[url("/images/no-comment.svg")] bg-contain bg-center bg-no-repeat' />
                  <span className='text-[12px] leading-[15px] text-text2 italic'>
                    Bài đăng chưa có bình luận
                  </span>
                </div>
              ) : (
                <Fragment>
                  <div className='flex flex-col items-start space-y-[12px]'>
                    {privateComments &&
                      map(privateComments, (comment, commentIndex) => (
                        <CommentTree
                          key={`${comment.id}-${commentIndex}`}
                          comment={comment}
                          post={post}
                          onCompleteCommenting={onCompleteCommenting}
                        />
                      ))}
                  </div>
                  {commentsData?.comments?.hasNextPage &&
                    lt(privateComments?.length, commentsData?.comments?.total) && (
                      <button
                        type='button'
                        className='flex justify-center py-[12px]'
                        onClick={() => setPage(page + 1)}
                      >
                        {loadingComments ? 'Đang tải thêm bình luận' : 'Xem thêm bình luận'}
                      </button>
                    )}
                </Fragment>
              )}
              {loadingComments && (
                <div className='flex items-center justify-center py-[12px]'>
                  <IconLoading className='min-h-[24px] min-w-[24px] animate-spin text-primary' />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='absolute bottom-0 right-0 flex h-[72px] w-[414px] justify-center items-center px-[12px] py-[18px] shadow-3'>
          <div className='flex w-full items-center'>
            <div className='flex items-center space-x-[10px] w-full'>
              <Link
                href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.SOCIAL_PROFILE}/${currentUser?.id}`}
              >
                <div className='h-[36px] w-[36px] cursor-pointer overflow-hidden rounded-full'>
                  <img
                    src={
                      (currentUser?.avatar as FileWithFullUrls)?.originalUrl ??
                      '/images/default-avatar.svg'
                    }
                    alt={currentUser?.fullName}
                    loading='lazy'
                    className='h-full w-full object-cover'
                  />
                </div>
              </Link>
              <div className='group w-full flex h-[40px] cursor-text items-center rounded-[12px] border border-stroke'>
                <input
                  ref={inputRef}
                  type='text'
                  value={bodyWatch}
                  placeholder='Nhập bình luận của bạn'
                  className='w-full bg-transparent px-[12px] py-[10px] placeholder-text2'
                  onChange={(event) => setValue('body', event.target.value)}
                  onKeyDown={keyDown}
                />
                {Boolean(bodyWatch) && (
                  <button
                    type='button'
                    className='mr-[12px] hidden group-hover:flex'
                    onClick={() => setValue('body', '')}
                  >
                    <IconClose className='h-[20px] w-[20px] text-text2' />
                  </button>
                )}
                <Popover className='relative'>
                  <Popover.Button
                    type='button'
                    className='flex items-center mr-[12px]'
                    onClick={() => setShowingEmoji(!showingEmoji)}
                  >
                    <IconSmile className='h-[18px] w-[18px] text-text2' />
                  </Popover.Button>
                  <Popover.Panel className='absolute bottom-[40px] right-0 cursor-pointer'>
                    <EmojiPicker
                      searchDisabled
                      previewConfig={{
                        showPreview: false,
                      }}
                      onEmojiClick={chooseEmoji}
                    />
                  </Popover.Panel>
                </Popover>
              </div>
              <button
                type='button'
                disabled={isEmpty(trim(bodyWatch))}
                className='flex h-[36px] min-w-[36px] items-center justify-center rounded-full bg-secondary transition duration-[200ms] ease-in-out hover:bg-secondary-light disabled:opacity-50 disabled:hover:bg-secondary'
              >
                <IconPaperPlane className='h-[22px] w-[22px] text-primary' />
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </Fragment>
  );
};

export default CommentModal;
